import React, { useCallback, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import AxiosUtil from '../../Utils/AxiosUtil';
import useAuth from '../../Hooks/useAuth';
import { ManagedReportItem, ManagedReportItemCustomization, ManagedReportItemInputMap } from '../Shared/ManagedReport';
import { ManagedChart } from '../Shared/ManagedChart';
import { ManagedDataSet } from '../Shared/ManagedDataSet';
import {
    ManagedFormRule,
    ManagedFormErrorInfo,
    ManagedApiError,
    ManagedAdminEditorMode,
    ManagedAdminActionMode,
    ManagedAdminChildOperation,
    ManagedActionModal,
    ManagedActionModalState,
    buildManagedActionModalState,
    SlimTenantAdminModel,
    mapChartAdminModel,
    mapDataSetAdminModel,
    mapSlimTenantAdminModel,
 } from '../Shared/ManagedHelpers';
import {
    AddIcon,
    Button,
    Checkbox,
    Col,
    DeleteIcon,
    Divider,
    EditIcon,
    Form,
    Input,
    Modal as ServaModal,
    Option,
    Row,
    Select,
    Space,
    Spin,
    Text,
    Title,
} from '@servahealth/serva-ui-kit';
import GetTenantAdminModelsRequest from '../../Interfaces/Requests/Administration/GetTenantAdminModelsRequest';
import GetTenantAdminModelsResponse from '../../Interfaces/Responses/Administration/GetTenantAdminModelResponse';
import GetDataSetAdminModelsWithChildrenRequest from '../../Interfaces/Requests/Administration/GetDataSetAdminModelsWithChildrenRequest';
import GetChartAdminModelsWithChildrenRequest from '../../Interfaces/Requests/Administration/GetChartAdminModelsWithChildrenRequest';
import GetDataSetAdminModelsWithChildrenResponse from '../../Interfaces/Responses/Administration/GetDataSetAdminModelsWithChildrenResponse';
import GetChartAdminModelsWithChildrenResponse from '../../Interfaces/Responses/Administration/GetChartAdminModelsWithChildrenResponse';
import Administration from '../../Constants/Endpoints/Administration';
import { CloseCircleOutlined } from '@ant-design/icons';
import AUDReportItemCustomizations, { ManagedReportItemCustomizationEditResult } from './AUDReportItemCustomizations';
import AUDReportItemInputMaps, { ManagedReportItemInputMapEditResult } from './AUDReportItemInputMaps';
import AUDDrilldowns, { ManagedDrilldownEditResult } from './AUDDrilldowns';

export type ManagedReportItemEditResult = {
    operation: ManagedAdminChildOperation;
    reportItem: ManagedReportItem;
};

type AUDReportItemFormValues = {
    riDataSetID: number;
    riChartID: number;
    riTenantID: number;
    riRefreshRate: number;
    riHideTitle: boolean;
};

type AUDReportItemsProps = {
    reportID: number;
    reportName: string;
    reportGroupName: string;
    reportPageID: number;
    reportPageTitle: string;
    reportItems: ManagedReportItem[];
    initialSelectedReportItemID?: number;
    initialMode?: ManagedAdminActionMode;
    handleSetReportItems: (
        result: ManagedReportItemEditResult
    ) => void;
    close: () => void;
};

const AUDReportItems: React.FC<AUDReportItemsProps> = ({ close, ...props }) => {
    const { user } = useAuth();
    const [formAUDReportItem] = Form.useForm();

    const reportItems = props.reportItems ?? [];

    const getInitialRIMode = (): ManagedAdminActionMode => {
        switch (props.initialMode) {
            case 'update':
                return 'update';
            case 'delete':
                return 'delete';
            case 'add':
            default:
                return 'add';
        }
    };

    const getInitialSelectedReportItem = (): ManagedReportItem | undefined => {
        if (!props.initialSelectedReportItemID || props.initialSelectedReportItemID <= 0) {
            return undefined;
        }

        return reportItems.find(
            (reportItem) => reportItem.id === props.initialSelectedReportItemID
        );
    };

    const initialSelectedReportItem = getInitialSelectedReportItem();
    const initialRIMode = getInitialRIMode();

    const initialRIFormValues = initialRIMode === 'add'
        ? {
            riDataSetID: 0,
            riChartID: 0,
            riTenantID: 0,
            riRefreshRate: 0,
            riHideTitle: false,
        }
        : {
            riDataSetID: initialSelectedReportItem?.dataSetId ?? 0,
            riChartID: initialSelectedReportItem?.chartId ?? 0,
            riTenantID: initialSelectedReportItem?.tenantId ?? 0,
            riRefreshRate: initialSelectedReportItem?.refreshRate ?? 0,
            riHideTitle: initialSelectedReportItem?.hideTitle ?? false,
        };

    const [addButtonClicked, setAddButtonClicked] = useState(initialRIMode === 'add');
    const [updButtonClicked, setUpdButtonClicked] = useState(initialRIMode === 'update');
    const [delButtonClicked, setDelButtonClicked] = useState(initialRIMode === 'delete');

    const riIncludeDisabled = false;

    const formatRIDisplayID = useCallback((id?: number | null): string => {
        return id && id > 0 ? String(id) : 'new';
    }, []);

    const riPropsReportID = props.reportID > 0 ? props.reportID : 0;
    const riPropsReportName = props.reportName !== '' ? props.reportName : 'New Report';
    const riPropsReportGroupName = props.reportGroupName !== '' ? props.reportGroupName : 'New Report Group';
    const riPropsReportPageID = props.reportPageID > 0 ? props.reportPageID : 0;
    const riPropsReportPageTitle = props.reportPageTitle !== '' ? props.reportPageTitle : 'New Report Page';

    const buildReportItemNameConstruct = useCallback((
        ri: ManagedReportItem | undefined,
        availableDataSets: ManagedDataSet[],
        availableCharts: ManagedChart[]
    ) => {
        if (!ri) {
            return '';
        }

        const dataSetName = availableDataSets.find((dataSet) => dataSet.id === (ri.dataSetId ?? 0))?.name ?? '';
        const chartTitle = availableCharts.find((chart) => chart.id === (ri.chartId ?? 0))?.title ?? '';

        return 'RPID: ' + formatRIDisplayID(riPropsReportPageID) +
            ' / DSID: [' + (ri.dataSetId ?? 0) + '] ' + dataSetName +
            ' / CHID: [' + (ri.chartId ?? 0) + '] ' + chartTitle + ' ';
    }, [formatRIDisplayID, riPropsReportPageID]);

    const [riSelectedReportItemID, setRISelectedReportItemID] = useState(initialSelectedReportItem?.id ?? -1);
    const [riSelectedReportItemTitle, setRISelectedReportItemTitle] = useState('');

    const [riDataSetID, setRIDataSetID] = useState<number | undefined>(initialSelectedReportItem?.dataSetId ?? 0);
    const [riChartID, setRIChartID] = useState<number | undefined>(initialSelectedReportItem?.chartId ?? 0);
    const [riTenantID, setRITenantID] = useState<number | undefined>(initialSelectedReportItem?.tenantId ?? 0);

    const [riSelectedReportItemCreatedBy, setRISelectedReportItemCreatedBy] = useState(initialSelectedReportItem?.createdBy ?? -1);
    const [riSelectedReportItemCreatedTs, setRISelectedReportItemCreatedTs] = useState(initialSelectedReportItem?.createdTs ?? new Date());

    const [riDetailsReportItemCustomizations, setRIDetailsReportItemCustomizations] = useState<ManagedReportItemCustomization[]>(
        initialSelectedReportItem?.reportItemCustomizations ?? []
    );
    const [riDetailsReportItemInputMaps, setRIDetailsReportItemInputMaps] = useState<ManagedReportItemInputMap[]>(
        initialSelectedReportItem?.reportItemInputMaps ?? []
    );

    const [riSelectedReportItemCustomizationID, setRISelectedReportItemCustomizationID] = useState(-1);
    const [riReportItemCustomizationEditorMode, setRIReportItemCustomizationEditorMode] = useState<ManagedAdminActionMode>('idle');

    const [riSelectedReportItemInputMapID, setRISelectedReportItemInputMapID] = useState(-1);
    const [riReportItemInputMapEditorMode, setRIReportItemInputMapEditorMode] = useState<ManagedAdminActionMode>('idle');

    const [manageReportItemCustomizationsModalOpen, setManageReportItemCustomizationsModalOpen] = useState(false);
    const [manageReportItemInputMapsModalOpen, setManageReportItemInputMapsModalOpen] = useState(false);
    const [manageReportItemDrilldownsModalOpen, setManageReportItemDrilldownsModalOpen] = useState(false);

    const [riSelectedDrilldownID, setRISelectedDrilldownID] = useState(-1);
    const [riDrilldownEditorMode, setRIDrilldownEditorMode] = useState<ManagedAdminActionMode>('idle');

    const [managedActionModalState, setManagedActionModalState] = useState<ManagedActionModalState>({
        open: false,
        mode: 'info',
        title: '',
        message: '',
        isError: false,
    });

    const handleRILoadError = (title: string, err: unknown) => {
        console.error(title + ': ', err);

        const apiError = err as ManagedApiError;

        const message = apiError?.response?.data?.message
            ? `${title}: ${apiError.response.data.message}`
            : `${title}: ${String(err)}`;

        setManagedActionModalState(
            buildManagedActionModalState(
                'info',
                'Report Item Load Error',
                message,
                true
            )
        );
    };

    const { data: tenants = [], isLoading: isGetTenantsLoading } = useQuery<GetTenantAdminModelsResponse[], Error, SlimTenantAdminModel[]>(
        ['getTenantAdminModelsRequest', riIncludeDisabled],
        async (): Promise<GetTenantAdminModelsResponse[]> => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_TENANT_ADMIN_MODELS}`;
                const requestData: GetTenantAdminModelsRequest = { gta_includeDisabled: riIncludeDisabled };
                return await AxiosUtil.get<GetTenantAdminModelsResponse[], GetTenantAdminModelsRequest>(url, requestData);
            } catch (err) {
                handleRILoadError('GET_TENANT_ADMIN_MODELS: [Get Tenant Admin Models] Internal Error', err);
                return Promise.reject(err);
            }
        },
        {
            onError: (err: Error) => {
                handleRILoadError('GET_TENANT_ADMIN_MODELS: [Get Tenant Admin Models] Internal Error', err);
            },
            select: (response: GetTenantAdminModelsResponse[]): SlimTenantAdminModel[] => {
                return response
                    .map(mapSlimTenantAdminModel)
                    .filter((eachTenant) => !eachTenant.deleted)
                    .sort((a, b) => a.name.localeCompare(b.name));
            },
        }
    );

    const { data: dataSets = [], isLoading: isGetDataSetsLoading } = useQuery<GetDataSetAdminModelsWithChildrenResponse[], Error, ManagedDataSet[]>(
        ['getDataSetAdminModelsWithChildrenRequest', riIncludeDisabled],
        async (): Promise<GetDataSetAdminModelsWithChildrenResponse[]> => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_DATASET_ADMIN_MODELS_WITH_CHILDREN}`;
                const requestData: GetDataSetAdminModelsWithChildrenRequest = { gdsac_includeDisabled: riIncludeDisabled };
                return await AxiosUtil.get<GetDataSetAdminModelsWithChildrenResponse[], GetDataSetAdminModelsWithChildrenRequest>(url, requestData);
            } catch (err) {
                handleRILoadError('GET_DATASET_ADMIN_MODELS_WITH_CHILDREN: [Get DataSet Admin Models With Children] Internal Error', err);
                return Promise.reject(err);
            }
        },
        {
            onError: (err: Error) => {
                handleRILoadError('GET_DATASET_ADMIN_MODELS_WITH_CHILDREN: [Get DataSet Admin Models With Children] Internal Error', err);
            },
            select: (response: GetDataSetAdminModelsWithChildrenResponse[]): ManagedDataSet[] => {
                return response
                    .map(mapDataSetAdminModel)
                    .sort((a, b) => a.name.localeCompare(b.name));
            },
        }
    );

    const { data: charts = [], isLoading: isGetChartsLoading } = useQuery<GetChartAdminModelsWithChildrenResponse[], Error, ManagedChart[]>(
        ['getChartAdminModelsWithChildrenRequest', riIncludeDisabled],
        async (): Promise<GetChartAdminModelsWithChildrenResponse[]> => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_CHART_ADMIN_MODELS_WITH_CHILDREN}`;
                const requestData: GetChartAdminModelsWithChildrenRequest = { gcac_includeDisabled: riIncludeDisabled };
                return await AxiosUtil.get<GetChartAdminModelsWithChildrenResponse[], GetChartAdminModelsWithChildrenRequest>(url, requestData);
            } catch (err) {
                handleRILoadError('GET_CHART_ADMIN_MODELS_WITH_CHILDREN: [Get Chart Admin Models With Children] Internal Error', err);
                return Promise.reject(err);
            }
        },
        {
            onError: (err: Error) => {
                handleRILoadError('GET_CHART_ADMIN_MODELS_WITH_CHILDREN: [Get Chart Admin Models With Children] Internal Error', err);
            },
            select: (response: GetChartAdminModelsWithChildrenResponse[]): ManagedChart[] => {
                return response
                    .map(mapChartAdminModel)
                    .filter((eachChart) => eachChart.deletedTs === undefined || eachChart.deletedTs === null)
                    .sort((a, b) => a.title.localeCompare(b.title));
            },
        }
    );

    useEffect(() => {
        if (!initialSelectedReportItem) {
            setRISelectedReportItemTitle('');
            return;
        }

        setRISelectedReportItemTitle(buildReportItemNameConstruct(initialSelectedReportItem, dataSets, charts));
    }, [buildReportItemNameConstruct, charts, dataSets, initialSelectedReportItem]);

    const isBlank = (value: unknown): boolean => {
        return value === undefined || value === null || String(value).trim().length === 0;
    };

    const validateRequiredRIField = (fieldName: string): Promise<void> => {
        const value = formAUDReportItem.getFieldValue(fieldName);
        return isBlank(value) ? Promise.reject() : Promise.resolve();
    };

    const validateRIDetailsData = (rule: ManagedFormRule, value: string) => {
        switch (rule.field) {
            case 'riTenantID':
            case 'riRefreshRate':
                return validateRequiredRIField(rule.field);

            case 'riDataSetID':
            case 'riChartID': {
                const newRIDataSetID = formAUDReportItem.getFieldValue('riDataSetID');
                const newRIChartID = formAUDReportItem.getFieldValue('riChartID');

                return ((!newRIDataSetID || newRIDataSetID === 0) && (!newRIChartID || newRIChartID === 0))
                    ? Promise.reject()
                    : Promise.resolve();
            }

            case 'riHideTitle':
                return Promise.resolve();

            default:
                return Promise.reject();
        }
    };

    const handleRIDataSetIDChange = (value: number) => {
        setRIDataSetID(value);
        formAUDReportItem.setFieldValue('riDataSetID', value);
    };

    const handleRIChartIDChange = (value: number) => {
        setRIChartID(value);
        formAUDReportItem.setFieldValue('riChartID', value);
    };

    const handleRITenantIDChange = (value: number) => {
        setRITenantID(value);
        formAUDReportItem.setFieldValue('riTenantID', value);
    };

    const setRIMode = (mode: ManagedAdminActionMode) => {
        setAddButtonClicked(mode === 'add');
        setUpdButtonClicked(mode === 'update');
        setDelButtonClicked(mode === 'delete');
    };

    const clearRISelection = () => {
        setRISelectedReportItemID(-1);
        setRISelectedReportItemTitle('');
        setRISelectedReportItemCreatedBy(0);
        setRISelectedReportItemCreatedTs(new Date());

        setRIDataSetID(0);
        setRIChartID(0);
        setRITenantID(0);

        setRIDetailsReportItemCustomizations([]);
        setRIDetailsReportItemInputMaps([]);
        setManageReportItemDrilldownsModalOpen(false);
        setRISelectedDrilldownID(-1);
        setRIDrilldownEditorMode('idle');
        setRIMode('add');
        handleRIReportItemResetForm('blank');
    };

    const handleRISelectionChange = (e: number) => {
        const selectedReportItem = reportItems.find((reportItem) => reportItem.id === e);

        setRISelectedReportItemID(e);
        setRISelectedReportItemTitle(buildReportItemNameConstruct(selectedReportItem, dataSets, charts));

        setRISelectedReportItemCreatedBy(selectedReportItem?.createdBy ?? 0);
        setRISelectedReportItemCreatedTs(selectedReportItem?.createdTs ?? new Date());

        setRIMode('add');
        handleRIReportItemResetForm('blank');
    };

    const setRIBlankForm = () => {
        setRIDataSetID(0);
        setRIChartID(0);
        setRITenantID(0);

        setRIDetailsReportItemCustomizations([]);
        setRIDetailsReportItemInputMaps([]);

        formAUDReportItem.setFieldValue('riDataSetID', 0);
        formAUDReportItem.setFieldValue('riChartID', 0);
        formAUDReportItem.setFieldValue('riTenantID', 0);
        formAUDReportItem.setFieldValue('riRefreshRate', 0);
        formAUDReportItem.setFieldValue('riHideTitle', false);
    };

    const setRIFormFromSelectedReportItem = () => {
        const selectedReportItem = reportItems.find(
            (reportItem) => reportItem.id === riSelectedReportItemID
        );

        const selectedDataSetID = selectedReportItem?.dataSetId ?? 0;
        const selectedChartID = selectedReportItem?.chartId ?? 0;
        const selectedTenantID = selectedReportItem?.tenantId ?? 0;

        setRIDataSetID(selectedDataSetID);
        setRIChartID(selectedChartID);
        setRITenantID(selectedTenantID);

        setRIDetailsReportItemCustomizations(selectedReportItem?.reportItemCustomizations ?? []);
        setRIDetailsReportItemInputMaps(selectedReportItem?.reportItemInputMaps ?? []);

        formAUDReportItem.setFieldValue('riDataSetID', selectedDataSetID);
        formAUDReportItem.setFieldValue('riChartID', selectedChartID);
        formAUDReportItem.setFieldValue('riTenantID', selectedTenantID);
        formAUDReportItem.setFieldValue('riRefreshRate', selectedReportItem?.refreshRate ?? 0);
        formAUDReportItem.setFieldValue('riHideTitle', selectedReportItem?.hideTitle ?? false);
    };

    const getRIOperation = (): ManagedAdminChildOperation => {
        if (addButtonClicked) return 'add';
        if (updButtonClicked) return 'update';
        return 'delete';
    };

    const getRIActionColor = (): string => {
        if (addButtonClicked) return 'cornflowerblue';
        if (updButtonClicked) return 'darkgray';
        if (delButtonClicked) return 'red';
        return 'black';
    };

    const handleRIReportItemResetForm = (mode: ManagedAdminEditorMode) => {
        if (mode === 'blank') {
            setRIBlankForm();
            return;
        }

        setRIFormFromSelectedReportItem();
    };

    const getSelectedReportItem = (): ManagedReportItem | undefined => {
        return reportItems.find(
            (reportItem) => reportItem.id === riSelectedReportItemID
        );
    };

    const getSelectedReportItemTitle = (): string => {
        return buildReportItemNameConstruct(getSelectedReportItem(), dataSets, charts);
    };

    const getReportItemCustomizationsEditorContext = () => {
        return {
            reportID: riPropsReportID,
            reportName: riPropsReportName,
            reportGroupName: riPropsReportGroupName,
            reportPageID: riPropsReportPageID,
            reportPageTitle: riPropsReportPageTitle,
            reportItemID: riSelectedReportItemID > 0 ? riSelectedReportItemID : 0,
            reportItemTitle: getSelectedReportItemTitle(),
            reportItemCustomizations: riDetailsReportItemCustomizations,
            initialSelectedReportItemCustomizationID: riSelectedReportItemCustomizationID,
            initialMode: riReportItemCustomizationEditorMode,
        };
    };

    const getReportItemInputMapsEditorContext = () => {
        return {
            reportID: riPropsReportID,
            reportName: riPropsReportName,
            reportGroupName: riPropsReportGroupName,
            reportPageID: riPropsReportPageID,
            reportPageTitle: riPropsReportPageTitle,
            reportItemID: riSelectedReportItemID > 0 ? riSelectedReportItemID : 0,
            reportItemTitle: getSelectedReportItemTitle(),
            reportItemDataSetID: getSelectedReportItem()?.dataSetId ?? 0,
            reportItemInputMaps: riDetailsReportItemInputMaps,
            initialSelectedReportItemInputMapID: riSelectedReportItemInputMapID,
            initialMode: riReportItemInputMapEditorMode,
        };
    };

    const getReportItemDrilldownsEditorContext = () => {
        const selectedReportItem = getSelectedReportItem();
        const selectedDataSet = dataSets.find((dataSet) => dataSet.id === (selectedReportItem?.dataSetId ?? 0));
        const selectedChart = charts.find((chart) => chart.id === (selectedReportItem?.chartId ?? 0));

        const selectedChartDataSetDrilldowns =
            selectedChart?.chartDataSets.flatMap((chartDataSet) => chartDataSet.drilldowns) ?? [];

        const selectedDataSetDrilldowns = selectedDataSet?.drilldowns ?? [];

        const reportItemDrilldowns =
            selectedChartDataSetDrilldowns.length > 0
                ? selectedChartDataSetDrilldowns
                : selectedDataSetDrilldowns;

        const availableSourceColumns =
            selectedChart && selectedChart.chartDataSets.length > 0
                ? selectedChart.chartDataSets
                    .map((chartDataSet) => chartDataSet.dataColumn)
                    .filter((dataColumn) => dataColumn.trim().length > 0)
                : selectedDataSet?.dataSetColumnFormats
                    .map((dataSetColumnFormat) => dataSetColumnFormat.columnName)
                    .filter((columnName) => columnName.trim().length > 0) ?? [];

        return {
            sourceReportItemID: riSelectedReportItemID > 0 ? riSelectedReportItemID : 0,
            sourceDataSetID: selectedChart?.dataSetId ?? selectedReportItem?.dataSetId ?? null,
            sourceTitle: getSelectedReportItemTitle(),
            sourceSubtitle:
                'Report: [' + formatRIDisplayID(riPropsReportID) + '] ' + riPropsReportGroupName + '-' + riPropsReportName +
                ' - Report Page: [' + formatRIDisplayID(riPropsReportPageID) + '] ' + riPropsReportPageTitle,
            drilldowns: reportItemDrilldowns,
            initialSelectedDrilldownID: riSelectedDrilldownID,
            initialMode: riDrilldownEditorMode,
            availableSourceColumns,
        };
    };

    const openRIReportItemDrilldownsEditor = (
        mode: ManagedAdminChildOperation,
        selectedDrilldownID: number = -1
    ) => {
        setRIDrilldownEditorMode(mode);
        setRISelectedDrilldownID(selectedDrilldownID);
        setManageReportItemDrilldownsModalOpen(true);
    };

    const closeRIReportItemDrilldownsEditor = () => {
        setManageReportItemDrilldownsModalOpen(false);
        setRIDrilldownEditorMode('idle');
        setRISelectedDrilldownID(-1);
    };

    const handleSetDrilldowns = (_result: ManagedDrilldownEditResult) => {
        setManagedActionModalState(
            buildManagedActionModalState(
                'info',
                'Report Item Drilldowns',
                'Report Item Drilldown edit results are not wired to the Report Item save model yet.',
                false
            )
        );
    };

    const onRIDrilldowns = () => {
        openRIReportItemDrilldownsEditor('add');
    };

    const openRIReportItemCustomizationsEditor = (
        mode: ManagedAdminChildOperation,
        selectedReportItemCustomizationID: number = -1
    ) => {
        setRIReportItemCustomizationEditorMode(mode);
        setRISelectedReportItemCustomizationID(selectedReportItemCustomizationID);
        setManageReportItemCustomizationsModalOpen(true);
    };

    const closeRIReportItemCustomizationsEditor = () => {
        setManageReportItemCustomizationsModalOpen(false);
        setRIReportItemCustomizationEditorMode('idle');
        setRISelectedReportItemCustomizationID(-1);
    };

    const onRICustomizations = () => {
        openRIReportItemCustomizationsEditor('add');
    };

    const openRIReportItemInputMapsEditor = (
        mode: ManagedAdminChildOperation,
        selectedReportItemInputMapID: number = -1
    ) => {
        setRIReportItemInputMapEditorMode(mode);
        setRISelectedReportItemInputMapID(selectedReportItemInputMapID);
        setManageReportItemInputMapsModalOpen(true);
    };

    const closeRIReportItemInputMapsEditor = () => {
        setManageReportItemInputMapsModalOpen(false);
        setRIReportItemInputMapEditorMode('idle');
        setRISelectedReportItemInputMapID(-1);
    };

    const onRIInputMaps = () => {
        openRIReportItemInputMapsEditor('add');
    };

    const buildManagedReportItemRequest = (values: AUDReportItemFormValues): ManagedReportItem => {
        return {
            id: addButtonClicked ? 0 : (updButtonClicked ? riSelectedReportItemID : (delButtonClicked ? riSelectedReportItemID : 0)),
            pageId: riPropsReportPageID,
            dataSetId: values.riDataSetID === 0 ? null : values.riDataSetID,
            chartId: values.riChartID === 0 ? null : values.riChartID,
            tenantId: values.riTenantID,
            refreshRate: values.riRefreshRate,
            createdBy: addButtonClicked ? (user?.id ?? 0) : (updButtonClicked ? riSelectedReportItemCreatedBy : (delButtonClicked ? riSelectedReportItemCreatedBy : 0)),
            modifiedBy: addButtonClicked ? (user?.id ?? 0) : (updButtonClicked ? (user?.id ?? 0) : (delButtonClicked ? (user?.id ?? 0) : 0)),
            deletedBy: addButtonClicked ? 0 : (updButtonClicked ? 0 : (delButtonClicked ? (user?.id ?? 0) : 0)),
            createdTs: addButtonClicked ? (new Date()) : (updButtonClicked ? riSelectedReportItemCreatedTs : (delButtonClicked ? riSelectedReportItemCreatedTs : undefined)),
            modifiedTs: addButtonClicked ? (new Date()) : (updButtonClicked ? (new Date()) : (delButtonClicked ? (new Date()) : undefined)),
            deletedTs: addButtonClicked ? (undefined) : (updButtonClicked ? (undefined) : (delButtonClicked ? (new Date()) : undefined)),
            hideTitle: values.riHideTitle,
            reportItemCustomizations: riDetailsReportItemCustomizations,
            reportItemInputMaps: riDetailsReportItemInputMaps,
        };
    };

    const handleCloseManagedActionModal = () => {
        setManagedActionModalState((currentState) => ({
            ...currentState,
            open: false,
        }));
    };

    const onFinishFailed = (errorInfo: ManagedFormErrorInfo<AUDReportItemFormValues>) => {
        console.error('AUDReportItems validation failed:', errorInfo);

        setManagedActionModalState(
            buildManagedActionModalState(
                'info',
                'Report Item Validation',
                'Please correct the highlighted Report Item fields and try again.',
                true
            )
        );
    };

    const onFinish = (values: AUDReportItemFormValues) => {
        const newManagedReportItem = buildManagedReportItemRequest(values);

        props.handleSetReportItems({
            operation: getRIOperation(),
            reportItem: newManagedReportItem,
        });

        close();
    };

    //=================================================================
    //=================================================================
    
    const handleRIAddReportItem = () => {
        clearRISelection();
    };

    //=================================================================
    //=================================================================

    const handleRIUpdReportItem = () => {
        setRIMode('update');
        handleRIReportItemResetForm('selected');
    };

    //=================================================================
    //=================================================================

    const handleRIDelReportItem = () => {
        setRIMode('delete');
        handleRIReportItemResetForm('selected');
    };

    //=================================================================
    //=================================================================

    const onRICancel = () => {
        clearRISelection();
    };

    const contentStyle: React.CSSProperties = {
        lineHeight: '95%',
        textAlign: 'center',
        color: 'black',
        backgroundColor: 'azure',
        borderRadius: '10px',
        border: `1px solid black`,
        marginTop: '16px',
        paddingRight: '8px',
        paddingLeft: '8px',
        height: '375px'
    };
    
    const handleSetReportItemCustomizations = (result: ManagedReportItemCustomizationEditResult) => {
        setRIDetailsReportItemCustomizations((currentReportItemCustomizations) => {
            switch (result.operation) {
                case 'add':
                    return [...currentReportItemCustomizations, result.reportItemCustomization];

                case 'update':
                    return currentReportItemCustomizations.map((currentReportItemCustomization) =>
                        currentReportItemCustomization.id === result.reportItemCustomization.id
                            ? result.reportItemCustomization
                            : currentReportItemCustomization
                    );

                case 'delete':
                    return currentReportItemCustomizations.filter(
                        (currentReportItemCustomization) => currentReportItemCustomization.id !== result.reportItemCustomization.id
                    );

                default:
                    return currentReportItemCustomizations;
            }
        });
    };

    const handleSetReportItemInputMaps = (result: ManagedReportItemInputMapEditResult) => {
        setRIDetailsReportItemInputMaps((currentReportItemInputMaps) => {
            switch (result.operation) {
                case 'add':
                    return [...currentReportItemInputMaps, result.reportItemInputMap];

                case 'update':
                    return currentReportItemInputMaps.map((currentReportItemInputMap) =>
                        currentReportItemInputMap.id === result.reportItemInputMap.id
                            ? result.reportItemInputMap
                            : currentReportItemInputMap
                    );

                case 'delete':
                    return currentReportItemInputMaps.filter(
                        (currentReportItemInputMap) => currentReportItemInputMap.id !== result.reportItemInputMap.id
                    );

                default:
                    return currentReportItemInputMaps;
            }
        });
    };

    return (
        <>
            <ManagedActionModal
                modalState={managedActionModalState}
                onOk={handleCloseManagedActionModal}
            />
            <ServaModal
                closeIcon={<CloseCircleOutlined></CloseCircleOutlined>}
                destroyOnHidden={true}
                hideButtons={true}
                width='60%'
                open={manageReportItemCustomizationsModalOpen}
                onCancel={closeRIReportItemCustomizationsEditor}
            >
                {
                    manageReportItemCustomizationsModalOpen &&
                    (
                        <AUDReportItemCustomizations
                            reportID={getReportItemCustomizationsEditorContext().reportID}
                            reportName={getReportItemCustomizationsEditorContext().reportName}
                            reportGroupName={getReportItemCustomizationsEditorContext().reportGroupName}
                            reportPageID={getReportItemCustomizationsEditorContext().reportPageID}
                            reportPageTitle={getReportItemCustomizationsEditorContext().reportPageTitle}
                            reportItemID={getReportItemCustomizationsEditorContext().reportItemID}
                            reportItemTitle={getReportItemCustomizationsEditorContext().reportItemTitle}
                            reportItemCustomizations={getReportItemCustomizationsEditorContext().reportItemCustomizations}
                            initialSelectedReportItemCustomizationID={getReportItemCustomizationsEditorContext().initialSelectedReportItemCustomizationID}
                            initialMode={getReportItemCustomizationsEditorContext().initialMode}
                            handleSetReportItemCustomizations={handleSetReportItemCustomizations}
                            close={closeRIReportItemCustomizationsEditor}
                        />
                    )
                }
            </ServaModal>
            <ServaModal
                closeIcon={<CloseCircleOutlined></CloseCircleOutlined>}
                destroyOnHidden={true}
                hideButtons={true}
                width='60%'
                open={manageReportItemInputMapsModalOpen}
                onCancel={closeRIReportItemInputMapsEditor}
            >
                {
                    manageReportItemInputMapsModalOpen &&
                    (
                        <AUDReportItemInputMaps
                            reportID={getReportItemInputMapsEditorContext().reportID}
                            reportName={getReportItemInputMapsEditorContext().reportName}
                            reportGroupName={getReportItemInputMapsEditorContext().reportGroupName}
                            reportPageID={getReportItemInputMapsEditorContext().reportPageID}
                            reportPageTitle={getReportItemInputMapsEditorContext().reportPageTitle}
                            reportItemID={getReportItemInputMapsEditorContext().reportItemID}
                            reportItemTitle={getReportItemInputMapsEditorContext().reportItemTitle}
                            reportItemDataSetID={getReportItemInputMapsEditorContext().reportItemDataSetID}
                            reportItemInputMaps={getReportItemInputMapsEditorContext().reportItemInputMaps}
                            initialSelectedReportItemInputMapID={getReportItemInputMapsEditorContext().initialSelectedReportItemInputMapID}
                            initialMode={getReportItemInputMapsEditorContext().initialMode}
                            handleSetReportItemInputMaps={handleSetReportItemInputMaps}
                            close={closeRIReportItemInputMapsEditor}
                        />
                    )
                }
            </ServaModal>
            <ServaModal
                closeIcon={<CloseCircleOutlined></CloseCircleOutlined>}
                destroyOnHidden={true}
                hideButtons={true}
                width='75%'
                open={manageReportItemDrilldownsModalOpen}
                onCancel={closeRIReportItemDrilldownsEditor}
            >
                {
                    manageReportItemDrilldownsModalOpen &&
                    (
                        <AUDDrilldowns
                            sourceType='reportItem'
                            sourceDataSetID={getReportItemDrilldownsEditorContext().sourceDataSetID}
                            sourceReportItemID={getReportItemDrilldownsEditorContext().sourceReportItemID}
                            sourceTitle={getReportItemDrilldownsEditorContext().sourceTitle}
                            sourceSubtitle={getReportItemDrilldownsEditorContext().sourceSubtitle}
                            drilldowns={getReportItemDrilldownsEditorContext().drilldowns}
                            initialSelectedDrilldownID={getReportItemDrilldownsEditorContext().initialSelectedDrilldownID}
                            initialMode={getReportItemDrilldownsEditorContext().initialMode}
                            availableSourceColumns={getReportItemDrilldownsEditorContext().availableSourceColumns}
                            handleSetDrilldowns={handleSetDrilldowns}
                            close={closeRIReportItemDrilldownsEditor}
                        />
                    )
                }
            </ServaModal>
            <Spin spinning={
                            isGetDataSetsLoading ||
                            isGetChartsLoading ||
                            isGetTenantsLoading
                           }>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '4px', width: '100%'}}>
                    <Text style={{height: '100%', width: '100%'}}>
                        <Title style={{backgroundColor: 'rgb(00, 55, 90)', borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}} level={4}>Manage Report Items</Title>
                    </Text>
                </Space>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '2px', width: '100%'}}>
                    <Text italic type='secondary'>{'Report: [' + formatRIDisplayID(riPropsReportID) + '] ' + riPropsReportGroupName + '-' + riPropsReportName}</Text>
                </Space>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '2px', width: '100%'}}>
                    <Text italic type='secondary'>{'Report Page: [' + formatRIDisplayID(riPropsReportPageID) + '] ' + riPropsReportPageTitle}</Text>
                </Space>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '2px', width: '100%'}}>
                    <Text italic type='secondary'>{'Report Item: [' + formatRIDisplayID(riSelectedReportItemID) + '] ' + riSelectedReportItemTitle}</Text>
                </Space>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={1} />
                    <Col span={22}>
                        <div>
                            <Space direction='horizontal' style={{width: '100%', justifyContent: 'left'}}>
                                <Text italic>Select an action below to proceed.</Text>
                            </Space>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                    <Col span={1} />
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={2} />
                    <Col span={8}>
                        <Text italic style={{float: 'right'}}>Select Report Item to Update or Delete:</Text>
                    </Col>
                    <Col span={14}>
                        <Select
                            styles={{ popup: { root: { width: 500 } } }}
                            filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                            placeholder={'Select a Report Item'}
                            showSearch
                            style={{paddingTop: '4px', width: '95%'}}
                            onChange={handleRISelectionChange}
                        >
                            {reportItems.map(
                                (eachReportItem) => {
                                    return (
                                        <Option
                                            key={eachReportItem.id}
                                            label={buildReportItemNameConstruct(eachReportItem, dataSets, charts)}
                                            value={eachReportItem.id}
                                        >
                                            {buildReportItemNameConstruct(eachReportItem, dataSets, charts)}
                                        </Option>
                                    );
                                }
                            )}
                        </Select>
                    </Col>
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={5}>
                        <Button
                            icon={<AddIcon />}
                            style={{ float: 'right' , marginBottom: '4px', width: '100%' }}
                            type='primary'
                            onClick={handleRIAddReportItem}
                        >
                            Add Report Item
                        </Button>
                    </Col>
                    <Col span={8} />
                    <Col span={5}>
                        <Button
                            disabled={riSelectedReportItemID === -1}
                            icon={<EditIcon />}
                            style={{ backgroundColor: 'darkgray', float: 'right', marginBottom: '4px', width: '100%' }}
                            type='primary'
                            onClick={handleRIUpdReportItem}
                        >
                            Update Report Item
                        </Button>
                    </Col>
                    <Col span={1} />
                    <Col span={5}>
                        <Button
                            disabled={riSelectedReportItemID === -1}
                            icon={<DeleteIcon />}
                            style={{ backgroundColor: 'red', float: 'right', marginBottom: '4px', width: '100%' }}
                            type='primary'
                            onClick={handleRIDelReportItem}
                        >
                            Delete Report Item
                        </Button>
                    </Col>
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={24}>
                        <div>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                </Row>
                <Form
                    form={formAUDReportItem}
                    initialValues={initialRIFormValues}
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                >
                    <div style={contentStyle}>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={21}>
                                <Divider orientation='left' orientationMargin={30} variant='dotted' style={{ borderColor: 'black', color: getRIActionColor() }}>
                                    {addButtonClicked ? 'Add Report Item' :
                                        (updButtonClicked ? 'Update Report Item: [' + formatRIDisplayID(riSelectedReportItemID) + '] ' :
                                            (delButtonClicked ? 'Delete Report Item: [' + formatRIDisplayID(riSelectedReportItemID) + '] ' : ''))}
                                </Divider>
                            </Col>
                            <Col span={3}>
                                <Button
                                    disabled={delButtonClicked}
                                    style={{ color: getRIActionColor(), fontStyle: 'italic' }}
                                    type='link'
                                    onClick={() => handleRIReportItemResetForm(addButtonClicked ? 'blank' : 'selected')}
                                >
                                    Reset Form
                                </Button>
                            </Col>
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={12}>
                                <div style={{textAlign:'left'}}>
                                    <Form.Item
                                        name='riDataSetID'
                                        rules={[
                                            {
                                                message: 'DataSet -or- Chart required.',
                                                validator: validateRIDetailsData,
                                            },
                                        ]}
                                        style={{ marginBottom: '0px' }}
                                    >
                                        <Select
                                            disabled={delButtonClicked}
                                            filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                                            label='DataSets:'
                                            optionFilterProp='label'
                                            showSearch
                                            value={riDataSetID}
                                            onChange={handleRIDataSetIDChange}
                                        >
                                            <Option
                                                value={0}
                                                label={'(Not Set)'}
                                            >
                                                {'(Not Set)'}
                                            </Option>
                                            {dataSets.map(
                                                (eachDataSet) => {
                                                    return (
                                                        <Option
                                                            key={eachDataSet.id}
                                                            label={eachDataSet.name}
                                                            value={eachDataSet.id}
                                                        >
                                                            {eachDataSet.name}
                                                        </Option>
                                                    );
                                                }
                                            )}
                                        </Select>
                                    </Form.Item>
                                </div>
                            </Col>
                            <Col span={12}>
                                <div style={{textAlign:'left'}}>
                                    <Form.Item
                                        name='riChartID'
                                        rules={[
                                            {
                                                message: 'DataSet -or- Chart required.',
                                                validator: validateRIDetailsData,
                                            },
                                        ]}
                                        style={{ marginBottom: '0px' }}
                                    >
                                        <Select
                                            disabled={delButtonClicked}
                                            filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                                            label='Charts:'
                                            optionFilterProp='label'
                                            showSearch
                                            value={riChartID}
                                            onChange={handleRIChartIDChange}
                                        >
                                            <Option
                                                value={0}
                                                label={'(Not Set)'}
                                            >
                                                {'(Not Set)'}
                                            </Option>
                                            {charts.map(
                                                (eachChart) => {
                                                    return (
                                                        <Option
                                                            key={eachChart.id}
                                                            label={eachChart.title}
                                                            value={eachChart.id}
                                                        >
                                                            {eachChart.title}
                                                        </Option>
                                                    );
                                                }
                                            )}
                                        </Select>
                                    </Form.Item>
                                </div>
                            </Col>
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={12}>
                                <div style={{textAlign:'left'}}>
                                    <Form.Item
                                        name='riTenantID'
                                        style={{ marginBottom: '0px' }}
                                    >
                                        <Select
                                            disabled={delButtonClicked}
                                            filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                                            label='Tenants:'
                                            optionFilterProp='label'
                                            showSearch
                                            value={riTenantID}
                                            onChange={handleRITenantIDChange}
                                        >
                                            <Option
                                                value={0}
                                                label={'(Not Set)'}
                                            >
                                                {'(Not Set)'}
                                            </Option>
                                            {tenants.map(
                                                (eachTenant) => {
                                                    return (
                                                        <Option
                                                            key={eachTenant.id}
                                                            label={eachTenant.name}
                                                            value={eachTenant.id}
                                                        >
                                                            {eachTenant.name}
                                                        </Option>
                                                    );
                                                }
                                            )}
                                        </Select>
                                    </Form.Item>
                                </div>
                            </Col>
                            <Col span={8}>
                                <div style={{textAlign: 'left', marginTop: '26px'}}>
                                    <Form.Item
                                        name='riRefreshRate'
                                        label='Refresh Rate'
                                        labelCol={{ span: 10, }}
                                        rules={[
                                            {
                                                message: 'Refresh Rate required.',
                                                required: true,
                                                validator: validateRIDetailsData,
                                            },
                                        ]}
                                        style={{ marginBottom: '0px' }}
                                    >
                                        <Input
                                            disabled={delButtonClicked}
                                            placeholder='Enter Refresh Rate'
                                            style={{color: delButtonClicked ? 'gray' : 'black', float: 'left', width: '75%'}}
                                            type='number'
                                        />
                                    </Form.Item>
                                </div>
                            </Col>
                            <Col span={4}>
                                <div style={{textAlign: 'left', marginTop: '26px'}}>
                                    <Form.Item
                                        key='riHideTitle'
                                        name='riHideTitle'
                                        valuePropName='checked'
                                        style={{alignItems:'center', marginBottom: '0px', marginTop: '0px'}}
                                    >
                                        <Checkbox
                                            defaultChecked={true}
                                            disabled={delButtonClicked}
                                        >
                                            Hide Title
                                        </Checkbox>
                                    </Form.Item>
                                </div>
                            </Col>
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={22}>
                                <div>
                                    <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                                </div>
                            </Col>
                            <Col span={1} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={6}>
                                <Form.Item
                                    key='ridetailscustomizations'
                                    name='riDetailsCustomizations'
                                    style={{margin: '4px'}}
                                >
                                    <Button
                                        style={{ width: '95%', backgroundColor: addButtonClicked ? 'cornflowerblue' : (updButtonClicked ? 'darkgray' : (delButtonClicked ? 'red' : 'black')) }}
                                        type='primary'
                                        onClick={onRICustomizations}
                                    >
                                        Report Item Customizations
                                    </Button>
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item
                                    key='ridetailsinputmaps'
                                    name='riDetailsInputMaps'
                                    style={{margin: '4px'}}
                                >
                                    <Button
                                        style={{ width: '95%', backgroundColor: addButtonClicked ? 'cornflowerblue' : (updButtonClicked ? 'darkgray' : (delButtonClicked ? 'red' : 'black')) }}
                                        type='primary'
                                        onClick={onRIInputMaps}
                                    >
                                        Report Item Input Maps
                                    </Button>
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item
                                    key='ridetailsdrilldowns'
                                    name='riDetailsDrilldowns'
                                    style={{margin: '4px'}}
                                >
                                    <Button
                                        disabled={riSelectedReportItemID === -1}
                                        style={{ width: '95%', backgroundColor: addButtonClicked ? 'cornflowerblue' : (updButtonClicked ? 'darkgray' : (delButtonClicked ? 'red' : 'black')) }}
                                        type='primary'
                                        onClick={onRIDrilldowns}
                                    >
                                        Report Item Drilldowns
                                    </Button>
                                </Form.Item>
                            </Col>
                            <Col span={6} />
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={1} />
                            <Col span={22}>
                                <div>
                                    <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                                </div>
                            </Col>
                            <Col span={1} />
                        </Row>
                    </div>
                    <Row gutter={16} className='retainbi-compact-row' style={{ paddingTop: '8px' }}>
                        <Col span={5}>
                            <Form.Item
                                name='riCloseButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    style={{ float: 'right', width: '95%' }}
                                    type='default'
                                    onClick={close}
                                >
                                    Close
                                </Button>
                            </Form.Item>
                        </Col>
                        <Col span={9} />
                        <Col span={5}>
                            <Form.Item
                                name='riApplyButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    htmlType='submit'
                                    style={{ backgroundColor: addButtonClicked ? 'cornflowerblue' : (updButtonClicked ? 'darkgray' : (delButtonClicked ? 'red' : 'black')), float: 'right', width: '95%' }}
                                    type='primary'
                                >
                                    {addButtonClicked ? 'Add Report Item' : (updButtonClicked ? 'Update Report Item' : (delButtonClicked ? 'Delete Report Item' : ''))}
                                </Button>
                            </Form.Item>
                        </Col>
                        <Col span={5}>
                            <Form.Item
                                name='riCancelButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    style={{ float: 'right', width: '95%' }}
                                    type='default'
                                    onClick={onRICancel}
                                >
                                    {addButtonClicked ? 'Cancel Add' : (updButtonClicked ? 'Cancel Update' : (delButtonClicked ? 'Cancel Delete' : ''))}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Spin>
        </>
    );
};

export default AUDReportItems;
